/**
 * groupPreference (HOF-106) — Ernteprognose's last selected crop lens,
 * remembered per tenant.
 *
 * **What is remembered is the crop GROUP, not `?cc=`.** The URL parameter holds
 * a `crop_cycle` id and the lens group is derived from it (`resolveSelectedGroup`),
 * but a crop cycle belongs to exactly one harvest season: next season's register
 * carries new ids and every stored one is dead. The durable analogue of
 * Preisspiegel's `?kat=` is the group key (`kulturGroupKey(crop, null)`) —
 * „Erdbeeren" survives the season rollover, and the page re-resolves it to that
 * season's first cycle through `firstCycleOfGroup`, which is the same expression
 * the default path already uses. Storing a cc id would give a preference with a
 * twelve-month shelf life and no way to tell that it had expired.
 *
 * `?s=` (the variety refinement), `?f=` (the Schlag) and the display axes
 * `?g=`/`?m=`/`?pm=`/`?h=`/`?py=`/`?d=` are out of scope — one primary selection
 * per page, the discipline HOF-101 set.
 */
import { createLensPreference } from '../../../utils/lensPreference';

/** Key prefix; the tenant id is appended. */
export const ERNTEPROGNOSE_GROUP_PREFERENCE_PREFIX = 'hofsync.ernteprognose.group';

const preference = createLensPreference(ERNTEPROGNOSE_GROUP_PREFERENCE_PREFIX);

export const ernteprognoseGroupPreferenceKey = preference.key;
export const readStoredGroup = preference.read;
export const writeStoredGroup = preference.write;

/**
 * The remembered crop group, but only if the lens still shows it.
 *
 * Validated against the VISIBLE groups — #2419's data-presence filter, and the
 * exact counterpart of HOF-101 validating a stored Kategorie against #2420's
 * data-bearing subtree. A crop the chart cannot draw this season has left the
 * lens, and restoring it would open a view with nothing in it. The unfiltered
 * `groups` set is deliberately not the test: it exists so that deep links keep
 * resolving, and a preference is not a deep link.
 */
export function resolveStoredGroup(
  stored: string | null,
  visibleGroups: readonly { groupKey: string }[],
): string | null {
  if (!stored) return null;
  return visibleGroups.some((g) => g.groupKey === stored) ? stored : null;
}
